import * as React from 'react';
import { TTextModel } from './helper/textPartHelperEffect';
import { Box } from '@mui/material';
import { theme } from '../../styles/theme';
import { HighLightedSpan } from './HighLightedSpan';
import { TextPartUtil } from './helper/textPartUtil';

export enum EHighlightedType {
    Highlighted,
    Normal
}

export type THighLightedTextProps = {
    text: string;
    highLighted: string[];
    highlightedStartPos?: number
    underscore?: boolean
    translated?: string;
}


export const HighLightedText: React.FC<THighLightedTextProps> = ({
    highLighted,
    text,
    highlightedStartPos,
    underscore,
    translated,
}) => {
    const [textParts, setTextParts] = React.useState<TTextModel[]>([]);

    React.useEffect(() => {
        let textParts = TextPartUtil.getTextParts(text, highLighted.join(''), highlightedStartPos || 0);
        setTextParts(textParts);
    }, [text, highLighted.join('')]);

    const ref = React.useRef(null);
    const debounceResize = React.useRef(null);
    React.useEffect(() => {
        const calcTransPosition = () => {
            if (debounceResize.current) clearTimeout(debounceResize.current);
            debounceResize.current = setTimeout(() => {
                const hlTranslated = Array.from(ref.current?.querySelectorAll('.hl-translated') || []);
                Array.from(ref.current?.querySelectorAll('.hl-start') || []).forEach((item, i) => {
                    const { offsetLeft = 0, offsetTop = 0 } = item as HTMLElement;
                    const tr = hlTranslated[i] as HTMLElement;
                    tr.style.textIndent = offsetLeft + 'px';
                    tr.style.top = offsetTop + 'px';
                });
            }, 100);
        };
        const resizeObserver = new ResizeObserver(calcTransPosition);
        if (translated && ref.current) resizeObserver.observe(ref.current);
        calcTransPosition();

        return () => {
            resizeObserver.disconnect();
        };
    }, [translated]);

    return (<Box
        ref={ref}
        component={translated ? 'div' : 'span'}
        sx={{
            position: 'relative',

            '.hl-translated': {
                position: 'absolute',
                zIndex: 10,
                marginTop: theme.spacing(-3.5),
                left: 0,
                fontSize: '11px',
                lineHeight: theme.spacing(3),
                color: theme.palette.primary.main,
                pointerEvents: 'none',
            },
    
            '.hl-start': {
                pointerEvents: 'none',
                opacity: 0,
                fontSize: '1px',
            },
        }}
    >
        {
            textParts.map((textPart, idx) => {
                const isStart = idx === 0;
                const isEnd = idx === textParts.length - 1;
                return textPart.type === EHighlightedType.Highlighted ?
                    <>
                        {!!translated && <>
                            <span className={'hl-translated'} >
                                {translated}
                            </span><span className={'hl-start'} >_</span></>}<HighLightedSpan
                            key={idx}
                            isStart={idx === 0 || (textPart[idx - 1] && textPart[idx - 1].type !== EHighlightedType.Highlighted)}
                            isEnd={idx === textParts.length -1 || (textPart[idx + 1] && textPart[idx + 1].type !== EHighlightedType.Highlighted)}
                            style={{
                                backgroundColor: theme.palette.info[100],
                            }}
                        >
                            {textPart.text}
                        </HighLightedSpan>
                    </>
                    :
                    <Box
                        key={idx}
                        component='span'
                        dangerouslySetInnerHTML={{ __html: textPart.text || '' }}
                        sx={{
                            padding: '4px 0',
                        }}
                    />;
            })
        }
    </Box>);
};
